import {add} from 'date-fns/add';
import {
  HTML_ATTR_RENDERIF,
  HTML_ATTR_REPLACE_VAR,
  HTML_CLASS_HIDE_ELEMENT,
} from '../const/html';
import {SELECTED_PRODUCT_HTML_BIDING} from '../const/htmlBindings';
import {FormatHelper} from './formatHelper';
import {getElements, setText} from './html';
import {ProductInfoV2} from './session';
import {ITimeService} from './timeService';

export interface IBindingService {
  updateSelectedProduct(product: ProductInfoV2): void;
  getElementsAvailableForVariant(variant: string | null): HTMLElement[];
  getElementsForLoggedInUserOnly(): HTMLElement[];
}

export class BindingService implements IBindingService {
  constructor(protected timeService: ITimeService) {}
  public getElementsAvailableForVariant(variant: string | null): HTMLElement[] {
    if (!variant) {
      return [];
    }
    return getElements(
      `[${HTML_ATTR_RENDERIF}~='purchase-variant-${variant}']`,
    );
  }

  public getElementsForLoggedInUserOnly(): HTMLElement[] {
    return getElements(`[${HTML_ATTR_RENDERIF}='user-loggedin']`);
  }

  public updateSelectedProduct(product: ProductInfoV2): void {
    this.updateProductName(product);
    this.updateProductOptionName(product);
    this.updateProductPrice(product);
    this.updateProductForcedCrossgrade(product);
    this.updateProductPerks(product);
    this.updateProductDuration(product);
    this.updateProductFirstPeriod(product);
  }

  private updateProductName(product: ProductInfoV2) {
    const elements = getElements(
      `[${HTML_ATTR_REPLACE_VAR}='${SELECTED_PRODUCT_HTML_BIDING.productName}']`,
    );
    for (const element of elements) {
      setText(element, product?.bpGroupName ?? '');
    }
  }

  private updateProductOptionName(product: ProductInfoV2) {
    const elements = getElements(
      `[${HTML_ATTR_REPLACE_VAR}='${SELECTED_PRODUCT_HTML_BIDING.productOfferName}']`,
    );
    for (const element of elements) {
      if (product?.blueprintName) {
        setText(element, product.blueprintName ?? '');
      }
    }
  }

  private updateProductPrice(product: ProductInfoV2) {
    // total price
    const price =
      typeof product.price.period.periodPrice === 'number'
        ? product.price.period.periodPrice
        : null;
    const priceText =
      product && price !== null
        ? FormatHelper.instance.getPriceFormatted(price, product.price.currency)
        : '';
    for (const e of getElements(
      `[${HTML_ATTR_REPLACE_VAR}='${SELECTED_PRODUCT_HTML_BIDING.periodPrice}']`,
    )) {
      setText(e, priceText);
    }

    // monthly price
    const pricePerMonth = product.price.displayPeriod.periodSingleIntervalPrice;
    const pricePerMonthText =
      product && pricePerMonth !== null
        ? FormatHelper.instance.getPriceFormatted(
            pricePerMonth,
            product.price.currency,
          )
        : '';
    for (const e of getElements(
      `[${HTML_ATTR_REPLACE_VAR}='${SELECTED_PRODUCT_HTML_BIDING.periodPricePerMonth}']`,
    )) {
      setText(e, pricePerMonthText);
    }

    // discount original price
    const discountOriginalPrice = product.price.discount;

    for (const e of getElements(
      `[${HTML_ATTR_RENDERIF}='${SELECTED_PRODUCT_HTML_BIDING.hasDiscount}']`,
    )) {
      if (discountOriginalPrice) {
        e.classList.remove(HTML_CLASS_HIDE_ELEMENT);
      } else {
        e.classList.add(HTML_CLASS_HIDE_ELEMENT);
      }
    }

    // negative binding
    for (const e of getElements(
      `[${HTML_ATTR_RENDERIF}='!${SELECTED_PRODUCT_HTML_BIDING.hasDiscount}']`,
    )) {
      if (discountOriginalPrice) {
        e.classList.add(HTML_CLASS_HIDE_ELEMENT);
      } else {
        e.classList.remove(HTML_CLASS_HIDE_ELEMENT);
      }
    }

    if (discountOriginalPrice) {
      const originalPrice =
        typeof discountOriginalPrice.normalPrice.periodPrice === 'number'
          ? discountOriginalPrice.normalPrice.periodPrice
          : null;

      const originalPriceText =
        product && originalPrice
          ? FormatHelper.instance.getPriceFormatted(
              originalPrice,
              product.price.currency,
            )
          : '';

      for (const e of getElements(
        `[${HTML_ATTR_REPLACE_VAR}='${SELECTED_PRODUCT_HTML_BIDING.periodPriceBeforeDiscount}']`,
      )) {
        setText(e, originalPriceText);
      }
      const originalMonthlyPrice =
        discountOriginalPrice.normalPrice.displayPeriodSingleIntervalPrice;
      const originalMonthlyPriceText =
        product && originalMonthlyPrice
          ? FormatHelper.instance.getPriceFormatted(
              originalMonthlyPrice,
              product.price.currency,
            )
          : '';
      for (const e of getElements(
        `[${HTML_ATTR_REPLACE_VAR}='${SELECTED_PRODUCT_HTML_BIDING.periodMonthlyPriceBeforeDiscount}']`,
      )) {
        setText(e, originalMonthlyPriceText);
      }
    }
  }

  private updateProductForcedCrossgrade(product: ProductInfoV2) {
    // price of a forced crossgrade if there is any
    const hasForcedCrossgrade =
      product.forcedModification &&
      product.forcedModification.type === 'crossgrade';

    for (const e of getElements(
      `[${HTML_ATTR_RENDERIF}='${SELECTED_PRODUCT_HTML_BIDING.hasForcedCrossgrade}']`,
    )) {
      if (hasForcedCrossgrade) {
        e.classList.remove(HTML_CLASS_HIDE_ELEMENT);
      } else {
        e.classList.add(HTML_CLASS_HIDE_ELEMENT);
      }
    }
    for (const e of getElements(
      `[${HTML_ATTR_RENDERIF}='!${SELECTED_PRODUCT_HTML_BIDING.hasForcedCrossgrade}']`,
    )) {
      if (hasForcedCrossgrade) {
        e.classList.add(HTML_CLASS_HIDE_ELEMENT);
      } else {
        e.classList.remove(HTML_CLASS_HIDE_ELEMENT);
      }
    }

    if (hasForcedCrossgrade) {
      if (product.forcedModification?.blueprint.price.currency) {
        const {currency, period} = product.forcedModification.blueprint.price;
        const newPrice = FormatHelper.instance.getPriceFormatted(
          period.periodPrice,
          currency,
        );
        for (const e of getElements(
          `[${HTML_ATTR_REPLACE_VAR}='${SELECTED_PRODUCT_HTML_BIDING.crossgradePrice}']`,
        )) {
          setText(e, newPrice);
        }
      }

      const crossgradeDurationText = product
        ? product.forcedModification?.blueprint.price.period
            .periodIntervalCount === 1
          ? 'Monat'
          : 'Monate'
        : '';
      for (const e of getElements(
        `[${HTML_ATTR_REPLACE_VAR}='${SELECTED_PRODUCT_HTML_BIDING.crossgradeDurationText}']`,
      )) {
        setText(e, crossgradeDurationText);
      }
    }
  }

  private updateProductPerks(product: ProductInfoV2) {
    const perkPercent = product.perk.percentSaved;
    const hasPerk = perkPercent !== undefined;
    for (const e of getElements(
      `[${HTML_ATTR_RENDERIF}='${SELECTED_PRODUCT_HTML_BIDING.hasPerk}']`,
    )) {
      if (hasPerk) e.classList.remove(HTML_CLASS_HIDE_ELEMENT);
      else e.classList.add(HTML_CLASS_HIDE_ELEMENT);
    }

    for (const e of getElements(
      `[${HTML_ATTR_REPLACE_VAR}='${SELECTED_PRODUCT_HTML_BIDING.perkPercent}']`,
    )) {
      setText(e, perkPercent ? perkPercent.toString() : '');
    }

    const comparePriceBase = product.perk.priceToCompareTo;
    const compPriceTxt =
      product && comparePriceBase !== undefined
        ? FormatHelper.instance.getPriceFormatted(
            comparePriceBase,
            product.price.currency,
          )
        : '';
    for (const e of getElements(
      `[${HTML_ATTR_REPLACE_VAR}='${SELECTED_PRODUCT_HTML_BIDING.comparativePrice}']`,
    )) {
      setText(e, compPriceTxt);
    }
  }

  private updateProductDuration(product: ProductInfoV2) {
    const durationTxtFriendly = product.price.displayPeriod.periodIntervalCount
      ? FormatHelper.instance.periodLengthDurationFriendly(
          product.price.displayPeriod.periodIntervalCount,
        )
      : '';
    for (const e of getElements(
      `[${HTML_ATTR_REPLACE_VAR}='${SELECTED_PRODUCT_HTML_BIDING.durationTextFriendly}']`,
    )) {
      setText(e, durationTxtFriendly);
    }

    for (const e of getElements(
      `[${HTML_ATTR_REPLACE_VAR}='${SELECTED_PRODUCT_HTML_BIDING.durationCount}']`,
    )) {
      setText(
        e,
        product.price.displayPeriod.periodIntervalCount.toString() ?? '',
      );
    }

    const durationText = product
      ? product.price.displayPeriod.periodIntervalCount === 1
        ? 'Monat'
        : 'Monate'
      : '';
    for (const e of getElements(
      `[${HTML_ATTR_REPLACE_VAR}='${SELECTED_PRODUCT_HTML_BIDING.durationText}']`,
    )) {
      setText(e, durationText);
    }

    // start date of a forced crossgrade if there is any
    if (
      product.forcedModification &&
      product.forcedModification.type === 'crossgrade'
    ) {
      for (const e of getElements(
        `[${HTML_ATTR_REPLACE_VAR}='${SELECTED_PRODUCT_HTML_BIDING.crossgradeStartOnMonth}']`,
      )) {
        const startAtMonth =
          product.price.displayPeriod.periodIntervalCount *
            product.forcedModification.afterRenewals +
          1;
        setText(e, startAtMonth);
      }
    }
  }

  private updateProductFirstPeriod(product: ProductInfoV2) {
    const hasTrial = product.price.firstPeriod?.periodPrice === 0;
    for (const element of getElements(
      `[${HTML_ATTR_REPLACE_VAR}='${SELECTED_PRODUCT_HTML_BIDING.firstPeriodLengthInDays}']`,
    )) {
      if (product.price.firstPeriod) {
        const {periodInterval, periodIntervalCount} =
          product.price.firstPeriod!;
        if (periodInterval === 'weekly') {
          setText(element, `${periodIntervalCount * 7}`);
        }
      }
    }

    for (const e of getElements(
      `[${HTML_ATTR_RENDERIF}='${SELECTED_PRODUCT_HTML_BIDING.hasTrial}']`,
    )) {
      if (hasTrial) {
        e.classList.remove(HTML_CLASS_HIDE_ELEMENT);
      } else {
        e.classList.add(HTML_CLASS_HIDE_ELEMENT);
      }
    }

    // negative binding
    for (const e of getElements(
      `[${HTML_ATTR_RENDERIF}='!${SELECTED_PRODUCT_HTML_BIDING.hasTrial}']`,
    )) {
      if (hasTrial) {
        e.classList.add(HTML_CLASS_HIDE_ELEMENT);
      } else {
        e.classList.remove(HTML_CLASS_HIDE_ELEMENT);
      }
    }

    for (const element of getElements(
      `[${HTML_ATTR_REPLACE_VAR}='${SELECTED_PRODUCT_HTML_BIDING.firstPeriodEndDate}']`,
    )) {
      if (product.price.firstPeriod) {
        const {periodInterval, periodIntervalCount} = product.price.firstPeriod;
        if (periodInterval === 'weekly') {
          const trialEndDate = add(this.timeService.getCurrentDate(), {
            weeks: periodIntervalCount,
          });
          const trialEndDateText =
            FormatHelper.instance.dateToDayAndMonth(trialEndDate);
          setText(element, trialEndDateText);
        }
      }
    }
  }
}
